import { useState, useEffect, useMemo } from 'react';
import { Search, List, LayoutGrid, Eye, Edit, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { listLoads, createLoad, updateLoad, getLoad } from '../../../entities/load';
import { listDrivers } from '../../../entities/driver';

const statusColors = {
  Delivered: 'bg-green-100 text-green-700',
  'In Transit': 'bg-yellow-100 text-yellow-700',
  'Awaiting Pickup': 'bg-gray-200 text-gray-700',
  'Picked Up': 'bg-blue-100 text-blue-700',
  Cancelled: 'bg-red-100 text-red-700',
};

const statusOptions = ['Awaiting Pickup', 'Picked Up', 'In Transit', 'Delivered', 'Cancelled'];

export function LoadsPage() {
  const [view, setView] = useState('card');
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Drivers for dropdown
  const [drivers, setDrivers] = useState([]);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyLoad = {
    origin: '',
    destination: '',
    cargo: '',
    driver_name: '',
    driver_id: '',
    rate: '',
    weight: '',
    pickup_date: '',
    delivery_date: '',
    status: 'Awaiting Pickup',
    customer_name: '',
    notes: '',
  };
  const [form, setForm] = useState(emptyLoad);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
    if (statusFilter !== 'all') params.set('status', statusFilter);
    params.set('sortBy', 'created_at');
    params.set('sortDir', 'desc');
    return `?${params.toString()}`;
  }, [page, limit, debouncedSearch, statusFilter]);

  const fetchLoads = async () => {
    setLoading(true);
    try {
      const res = await listLoads(queryString);
      setLoads(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(Math.ceil((res.total || 0) / limit));
    } catch (err) {
      console.error('Failed to load loads:', err);
      toast.error('Failed to load loads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  // Load drivers for dropdown
  useEffect(() => {
    listDrivers('?limit=100&status=active')
      .then(res => setDrivers(res.data || []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDriverSelect = (e) => {
    const selectedId = e.target.value;
    const driver = drivers.find(d => d.id === selectedId);
    setForm(prev => ({
      ...prev,
      driver_id: selectedId,
      driver_name: driver ? driver.name : '',
    }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyLoad);
    setIsModalOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setIsModalOpen(true);
    try {
      const load = await getLoad(id);
      setForm({
        origin: load.origin || '',
        destination: load.destination || '',
        cargo: load.cargo || '',
        driver_name: load.driver_name || '',
        driver_id: load.driver_id || '',
        rate: load.rate || '',
        weight: load.weight || '',
        pickup_date: load.pickup_date || '',
        delivery_date: load.delivery_date || '',
        status: load.status || 'Awaiting Pickup',
        customer_name: load.customer_name || '',
        notes: load.notes || '',
      });
    } catch (err) {
      toast.error('Failed to load details');
      setIsModalOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.origin.trim() || !form.destination.trim()) {
      toast.error('Origin and destination are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateLoad(editingId, form);
        toast.success('Load updated');
      } else {
        await createLoad(form);
        toast.success('Load created');
      }
      setIsModalOpen(false);
      setForm(emptyLoad);
      setEditingId(null);
      fetchLoads();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save load';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const formatRate = (rate) => {
    const num = Number(rate || 0);
    return `$${num.toLocaleString()}`;
  };

  return (
    <div className="p-6 w-full">
      {/* Header + Filters */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search loads..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>

          <button onClick={() => setView('list')} className={`p-2 rounded-lg border ${view === 'list' ? 'bg-blue-50 border-blue-500 text-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
            <List className="w-4 h-4" />
          </button>
          <button onClick={() => setView('card')} className={`p-2 rounded-lg border ${view === 'card' ? 'bg-blue-50 border-blue-500 text-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
            <LayoutGrid className="w-4 h-4" />
          </button>

          <select
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
          >
            <option value="all">All Statuses</option>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <button onClick={openAdd} className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          + New Load
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading loads...</div>
      ) : loads.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No loads found.</div>
      ) : (
        <>
          {/* Card View */}
          {view === 'card' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loads.map((load) => (
                <div key={load.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800">{load.load_id}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[load.status] || ''}`}>{load.status}</span>
                  </div>
                  <p className="text-sm text-gray-600"><span className="font-medium">Route:</span> {load.origin} - {load.destination}</p>
                  <p className="text-sm text-gray-600"><span className="font-medium">Cargo:</span> {load.cargo || '-'}</p>
                  <p className="text-sm text-gray-600"><span className="font-medium">Driver:</span> {load.driver_name || '-'}</p>
                  <p className="text-sm text-gray-600"><span className="font-medium">Rate:</span> {formatRate(load.rate)}</p>
                  <p className="text-sm text-gray-600"><span className="font-medium">Pickup:</span> {load.pickup_date || '-'}</p>
                  <p className="text-sm text-gray-600 mb-3"><span className="font-medium">Delivery:</span> {load.delivery_date || '-'}</p>
                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    <button onClick={() => openEdit(load.id)} className="flex-1 border border-gray-300 rounded-md py-1 text-sm hover:bg-gray-100">Edit</button>
                    <button className="flex-1 border border-gray-300 rounded-md py-1 text-sm hover:bg-gray-100">Route</button>
                    <button className="flex-1 border border-gray-300 rounded-md py-1 text-sm hover:bg-gray-100">Docs</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table View */}
          {view === 'list' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium">
                  <tr>
                    <th className="px-6 py-3">LOAD ID</th>
                    <th className="px-6 py-3">ROUTE</th>
                    <th className="px-6 py-3">DRIVER</th>
                    <th className="px-6 py-3">RATE</th>
                    <th className="px-6 py-3">STATUS</th>
                    <th className="px-6 py-3">DATE & TIME</th>
                    <th className="px-6 py-3">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loads.map((load) => (
                    <tr key={load.id} className="border-t">
                      <td className="px-6 py-3 font-semibold text-gray-800">{load.load_id}</td>
                      <td className="px-6 py-3">{load.origin} - {load.destination}</td>
                      <td className="px-6 py-3">{load.driver_name || '-'}</td>
                      <td className="px-6 py-3 font-medium">{formatRate(load.rate)}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[load.status] || ''}`}>{load.status}</span>
                      </td>
                      <td className="px-6 py-3">{load.pickup_date || '-'}</td>
                      <td className="px-6 py-3 flex gap-2">
                        <Eye className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-600" />
                        <Edit className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-600" onClick={() => openEdit(load.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
                <span className="text-sm text-gray-500">
                  Showing <span className="font-medium">{loads.length === 0 ? 0 : (page - 1) * limit + 1}-{Math.min(page * limit, total)}</span> of {total}
                </span>
                <div className="flex items-center gap-1">
                  <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&lt;</button>
                  <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{page}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&gt;</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editingId ? 'Edit Load' : 'Add New Load'}</h2>
              <button onClick={() => { if (!saving) { setIsModalOpen(false); setEditingId(null); setForm(emptyLoad); } }}>
                <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Origin" name="origin" value={form.origin} onChange={handleChange} required placeholder="Los Angeles, CA" />
                <FormField label="Destination" name="destination" value={form.destination} onChange={handleChange} required placeholder="Fresno, CA" />
                <FormField label="Cargo" name="cargo" value={form.cargo} onChange={handleChange} placeholder="Furniture" />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                  <select name="driver_id" value={form.driver_id} onChange={handleDriverSelect} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select driver</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <FormField label="Rate ($)" name="rate" value={form.rate} onChange={handleChange} type="number" placeholder="2300" />
                <FormField label="Weight" name="weight" value={form.weight} onChange={handleChange} placeholder="12,000 lbs" />
                <FormField label="Pickup Date" name="pickup_date" type="date" value={form.pickup_date} onChange={handleChange} />
                <FormField label="Delivery Date" name="delivery_date" type="date" value={form.delivery_date} onChange={handleChange} />
                <FormField label="Customer" name="customer_name" value={form.customer_name} onChange={handleChange} placeholder="ABC Retail" />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select name="status" value={form.status} onChange={handleChange} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { if (!saving) { setIsModalOpen(false); setEditingId(null); setForm(emptyLoad); } }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Save Load'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, name, value, onChange, type = 'text', required = false, placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}
